/**
 * Billing page (round7/billing-ui, P4-B) presentation/derivation helpers.
 * `TenantConfig`/`BillingPrice`/etc. (the backend contract) live in api.ts;
 * this module owns the pure "given the contract, what should the page show"
 * logic — same split as leads.ts (Lead/LeadStage types in api.ts, stage
 * presentation metadata here).
 *
 * === The "never branch on a raw tier string" rule ===
 * Every function below that describes the TENANT'S OWN CURRENT plan
 * (entitlementFacts, describeUsage, formatUnixSeconds) derives its output
 * ONLY from `entitlements`/`usage`/`currentPeriodEnd` — never a tier name,
 * because GET /api/portal/tenant doesn't even return one (see this repo's
 * CLAUDE.md Round 1 rule, and BillingPage.tsx's own header). The ONE place
 * "mid"/"top" strings appear at all is `tierLabel`, used ONLY to label the
 * no-subscription PLAN-PICKER grid (a product catalog necessarily has to
 * name what it's selling) — a categorically different thing from
 * reverse-engineering or displaying "which plan is this tenant currently
 * on," which this module never does anywhere.
 */
import type { BillingInterval, BillingPrice, BillingTier, TenantConfig, UsageResult } from "./api";

// === Billing view state ======================================================
// Which of the P4-B/P4.5-B states (docs/planning/DockPro-Phase4-Revised-
// Prompts.md's P4-B section and DockPro-Phase4.5-Trial-SelfReg-Prompts.md's
// P4.5-B section, as amended) BillingPage.tsx should render.
//
// Two flag-not-kind precedents, both deliberate, both explained in full
// where they're actually decided (deriveBillingViewState's own doc comment
// below):
//   - "cancels-at-period-end" is a flag on "active" (not its own `kind`) —
//     it's the same underlying Stripe status ("active") with one additional
//     boolean, and the entitlements facts / usage-this-month a tenant
//     already has SHOULD still show while they're mid-cancellation (a
//     synthesis of the spec, not an explicit requirement — nothing says to
//     hide them, and a tenant about to lose access has every reason to
//     still see what they currently get) — only the renewal line's wording
//     changes (see BillingPage.tsx's rendering of this flag).
//   - round7/signup-ui (P4.5-B): "lapsed trial" is likewise a flag on
//     "no-subscription" (`lapsedTrial`), not its own `kind` — mirroring the
//     cancels-at-period-end precedent immediately above rather than
//     inventing a new shape for a similarly small variation: it's the same
//     underlying rendering (the plan-picker grid, wired to the same
//     checkout flow) with one additional boolean that only changes the
//     subtitle copy above the grid. See deriveBillingViewState's doc comment
//     for exactly which tenants land here with the flag true vs false.
export type BillingViewState =
  | { kind: "parent-org" }
  // 2026-09-10: `pendingCard` is a second flag on the same kind (same
  // flag-not-kind precedent as lapsedTrial, for the same reason: same grid,
  // same checkout flow, different words above it). True for a tenant who
  // registered, was sent to Stripe Checkout, and left before adding a card
  // — the account exists but no trial has ever started. Never true together
  // with lapsedTrial: nothing has lapsed for these tenants.
  | { kind: "no-subscription"; lapsedTrial: boolean; pendingCard: boolean }
  | { kind: "past_due" }
  | { kind: "active"; cancelAtPeriodEnd: boolean }
  // round7/signup-ui (P4.5-B) — a self-registered tenant still inside its
  // 30-day no-card trial (D-4/D-5). `daysRemaining` is always >= 0 (clamped
  // — see daysRemainingFromTrialEndsAt below) and computed from
  // `trialEndsAt`, NEVER from Stripe's own `trial_end` (that field doesn't
  // exist on this contract at all — see api.ts's TenantConfig.trialEndsAt
  // doc comment for why the two are deliberately independent).
  | { kind: "trialing"; daysRemaining: number };

/**
 * Derives which state to render from the authed tenant response.
 *
 * === round7/signup-ui (P4.5-B): two new branches, and where they sit ===
 * `status: "trialing"` was already a value this function saw (`TenantStatus`
 * has included it since before this round), but until now it fell through
 * to the SAME ambiguous "active or no-subscription?" `currentPeriodEnd`
 * check `status === "active"` uses — see the REAL GAP section below. That
 * was correct at the time (nothing WROTE `status: "trialing"` yet, so the
 * branch was dead code in practice), but it no longer is:
 * `registerHandler.ts`'s self-registration path now sets it, and unlike
 * bare "active", **"trialing" is never a provisioning artifact** —
 * `provisionTenant.ts` (the admin CLI) never produces it, only self-
 * registration does. So it gets its own explicit, unambiguous branch rather
 * than continuing to share the currentPeriodEnd-presence check:
 *
 *   - **Precedence vs. `billedAt === "parent-org"`**: parent-org still wins,
 *     checked first, completely unconditionally — reasoned through
 *     explicitly here rather than assumed, because it's the one case that
 *     ISN'T settled by the two fields being structurally exclusive (see the
 *     past_due point below). Today, a trialing tenant's org is always
 *     `kind: "direct"` (registerHandler.ts Step 3 creates it that way
 *     unconditionally) and `resolveBillingContext` can only resolve a
 *     `direct` org to `billedAt: "tenant-org"` — so `{status: "trialing",
 *     billedAt: "parent-org"}` cannot occur via any write path in this
 *     codebase today. But `billedAt` and `status` are independent fields on
 *     the response (nothing enforces they can never combine that way at the
 *     TYPE level, and a future enterprise-org path is exactly the kind of
 *     thing that could one day put a child tenant in a trial under a parent
 *     that owns its billing), and the EXISTING parent-org precedent is
 *     already "wins over everything else, unconditionally" for exactly this
 *     reason — a tenant whose billing is managed by a different org has
 *     nothing of its own to show on this page, REGARDLESS of what its own
 *     `status` says. Carving out an exception for "unless status is
 *     trialing" would mean inventing a second rule with no product reason
 *     behind it. So parent-org keeps its existing, tested "checked first,
 *     unconditionally" position, now with a test asserting it also beats
 *     `status: "trialing"` specifically (see billing.test.ts).
 *   - **Precedence vs. `status === "past_due"`**: this one is NOT actually a
 *     precedence question, because `status` is a single field with a closed
 *     set of mutually-exclusive string values — a tenant literally cannot
 *     be BOTH `"trialing"` and `"past_due"` at once (confirmed by the shape
 *     of the type itself, and by the product mechanics: D-4's no-card trial
 *     never collects a payment method, so there is no invoice for Stripe to
 *     ever mark `invoice.payment_failed` against while trialing — `past_due`
 *     can only arise AFTER a real card is on file and a real charge is
 *     attempted). Whichever of these two `if` branches is written first,
 *     the other can never fire for the same tenant — an `if`/`else if`
 *     chain testing equality against different values of the same field is
 *     order-independent by construction. Placed right after past_due purely
 *     for readability (grouping the `status`-keyed branches together before
 *     falling through to the ambiguous cases), not because ordering changes
 *     any outcome.
 *   - **What "trialing" no longer shares with "active"**: previously,
 *     `"trialing"` fell through to the same `currentPeriodEnd`/
 *     `cancelAtPeriodEnd` presence check `"active"` uses, because — like
 *     bare "active" — it wasn't yet informative by itself. That reasoning
 *     no longer applies to trialing specifically: it now has exactly one
 *     real-world cause (self-registration) and is never ambiguous, so it
 *     gets its own unconditional branch instead of waiting on a Stripe
 *     webhook signal it doesn't need. `"active"` alone remains ambiguous
 *     and keeps falling through to the existing check, unchanged.
 *
 * === round7/signup-ui (P4.5-B): the lapsed-trial / never-subscribed split ===
 * `trialEndsAt` (api.ts's TenantConfig doc comment) is written ONCE at
 * registration and NEVER changes afterward — including after the trial
 * lapses to `status: "canceled"` (D-6) — which is exactly what makes its
 * PRESENCE, independent of the tenant's current status, a reliable marker
 * of "this tenant registered through self-signup at some point," usable
 * long after the trial itself is over. Folded into the EXISTING
 * unpaid/canceled/suspended branch as a boolean flag (`lapsedTrial`) rather
 * than a new `kind`, mirroring the established `cancelAtPeriodEnd`-on-
 * "active" precedent (see BillingViewState's own doc comment) — both are
 * "same underlying rendering, one boolean changes the copy," not a
 * different page shape. Deliberately NOT computed in the final ambiguous
 * "active-but-no-confirmed-subscription" fallback further down, even though
 * a tenant could theoretically reach it with a `trialEndsAt` on file (e.g. a
 * trial that converted and is now genuinely active): `status` there is
 * literally `"active"`, not a terminal/no-access status, so calling it a
 * "lapsed trial" would be actively wrong, not just imprecise —
 * `trialEndsAt` is a permanent historical fact and is correctly IGNORED
 * once a tenant is genuinely active, exactly as api.ts's doc comment says
 * it should be.
 *
 * === A REAL GAP, found while building this: `status` cannot distinguish
 * "never checked out" from "actively subscribed" ===
 * The spec's own words for no-subscription are "no active/trialing/past_due
 * signal — basically the default state for a tenant that's never checked
 * out," which reads as if `status` itself carries that signal. It doesn't:
 * `scripts/provisionTenant.ts` (DockPro-Backend) stamps EVERY newly
 * provisioned tenant with `status: "active"` unconditionally, with zero
 * Stripe involvement — and `checkout.session.completed` itself never writes
 * `status` either (only `tier`), so completing a real checkout doesn't
 * change it. `status: "active"` is therefore the literal SAME value for a
 * tenant that has never touched Stripe and one with a genuinely healthy paid
 * subscription — confirmed against DockPro-Backend's own fixtures
 * (portalTenantHandler.test.ts, submitHandler.test.ts: every "everything's
 * fine" test tenant is constructed with `status: "active"` directly, no
 * checkout involved) and `@dockpro/core`'s `tenantHasAccess()`, which
 * already treats a never-subscribed tenant as fully "has access" the moment
 * it's provisioned. Branching this page's no-subscription/active split on
 * `status` alone, as the spec's wording literally suggests, would show
 * EVERY tenant that has never subscribed as "active" — the opposite of what
 * this state is for.
 *
 * The one signal that DOES distinguish them: `currentPeriodEnd`/
 * `cancelAtPeriodEnd` are written ONLY once a `customer.subscription.*`
 * webhook has actually landed for that tenant (see api.ts's TenantConfig doc
 * comment) — never by provisioning, never by `checkout.session.completed`
 * itself. Their presence is this page's real proxy for "a subscription
 * relationship exists." The known trade-off (already documented in
 * DockPro-Backend's DEBT.md, round7/billing-ui-backend entry, for a
 * different reason): in the narrow window between a real checkout and the
 * first `.updated` event, a genuinely-just-subscribed tenant is briefly
 * indistinguishable from a never-subscribed one and sees the plan picker
 * again — a real but small, honest, self-correcting gap (it resolves itself
 * the moment the webhook lands), and a strictly safer failure mode than the
 * alternative (showing fabricated "active" entitlements to a tenant who has
 * never paid). Flagged here rather than silently implemented against the
 * spec's literal (but, per the above, inaccurate) wording.
 *
 * Order (updated, round7/signup-ui):
 *   1. billedAt === "parent-org" wins over EVERYTHING else, per the P4-0
 *      amendment's explicit instruction — checked first, unconditionally.
 *      Now explicitly includes beating status === "trialing" too (see the
 *      precedence discussion above) — not just past_due/active as before.
 *   2. status === "past_due" next — a payment problem is real and worth
 *      surfacing regardless of the currentPeriodEnd ambiguity above (a
 *      past_due status can only be set by a real Stripe invoice.payment_
 *      failed event, so unlike bare "active" it's never a provisioning
 *      artifact).
 *   3. status === "trialing" next (NEW) — always unambiguous (see above),
 *      so it renders directly with however many days remain, never falling
 *      through to the currentPeriodEnd check step 5 uses. Ordered right
 *      after past_due for readability only; the two are mutually exclusive
 *      values of the same field, so this position is not load-bearing (see
 *      above for the full reasoning).
 *   4. status === "unpaid" | "canceled" | "suspended" next, UNCONDITIONALLY
 *      to no-subscription — even if a stale currentPeriodEnd/
 *      cancelAtPeriodEnd is still sitting on the row from before the
 *      subscription ended, showing "active" facts for a tenant with no
 *      current access would be actively misleading. None of these three get
 *      their own bullet in the original P4-B spec; routing them to "here's
 *      how to get a working subscription" is the one rendering that never
 *      dead-ends the page. NEW (round7/signup-ui): now carries
 *      `lapsedTrial: trialEndsAt !== undefined` — see the lapsed-trial
 *      discussion above.
 *   5. Everything else (status "active" — the one value still ambiguous by
 *      itself, per the gap below, now that trialing has its own branch
 *      above) — check currentPeriodEnd/cancelAtPeriodEnd presence: present
 *      means a subscription webhook has genuinely landed, so render
 *      "active"; absent means no confirmed subscription yet, so render
 *      "no-subscription" (lapsedTrial: false — unconditionally, per the
 *      lapsed-trial discussion above: a status of literal "active" is never
 *      a lapsed trial, regardless of what trialEndsAt says).
 */
export function deriveBillingViewState(
  tenant: Pick<TenantConfig, "billedAt" | "status" | "currentPeriodEnd" | "cancelAtPeriodEnd" | "trialEndsAt">,
  now: number = Date.now(),
): BillingViewState {
  if (tenant.billedAt === "parent-org") return { kind: "parent-org" };
  if (tenant.status === "past_due") return { kind: "past_due" };
  // Card-gated trial (2026-09-10): the owner can reach the portal while
  // the card is outstanding — that is how they finish checkout — but
  // nothing tenant-facing is being served yet, so this is emphatically NOT
  // "trialing" (no trial has started, and trialEndsAt is absent until
  // Checkout completes). It renders as the plan picker, with no lapsed-
  // trial framing: nothing has lapsed, it was simply never started.
  if (tenant.status === "pending_card") {
    return { kind: "no-subscription", lapsedTrial: false, pendingCard: true };
  }
  if (tenant.status === "trialing") {
    return { kind: "trialing", daysRemaining: daysRemainingFromTrialEndsAt(tenant.trialEndsAt, now) };
  }
  if (tenant.status === "unpaid" || tenant.status === "canceled" || tenant.status === "suspended") {
    return { kind: "no-subscription", lapsedTrial: tenant.trialEndsAt !== undefined, pendingCard: false };
  }
  const hasConfirmedSubscription =
    tenant.currentPeriodEnd !== undefined || tenant.cancelAtPeriodEnd !== undefined;
  if (hasConfirmedSubscription) {
    return { kind: "active", cancelAtPeriodEnd: tenant.cancelAtPeriodEnd === true };
  }
  return { kind: "no-subscription", lapsedTrial: false, pendingCard: false };
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Clamped at zero, never negative — a self-registered tenant from before
// H-2 has NO real Stripe subscription driving an automatic day-30
// cancellation (see DockPro-Backend's DEBT.md, round7/self-registration
// entry's "second, related, NEW accepted gap"), so `status` can legitimately
// stay "trialing" indefinitely past the tenant's own `trialEndsAt` — this
// must render "0 days left", never a negative count. Absent `trialEndsAt`
// (should not happen for a genuinely "trialing" tenant — registerHandler.ts
// always writes both together in the same request — but TenantConfig types
// it optional, and this function must still return SOMETHING) fails closed
// to 0 rather than fabricating a number, matching this codebase's general
// "never guess on ambiguous input" posture (see e.g. DockPro-Backend's
// preAuthEmailVerifiedGate.ts). `now` is an injectable parameter (default
// `Date.now()`) purely so tests can assert a specific day count without
// being sensitive to real wall-clock time or a day-boundary race.
function daysRemainingFromTrialEndsAt(trialEndsAt: number | undefined, now: number): number {
  if (trialEndsAt === undefined) return 0;
  return Math.max(0, Math.ceil((trialEndsAt - now) / MS_PER_DAY));
}

// Shared copy for the trialing state's banner — used both by BillingPage.tsx
// (the trialing state's own in-page banner) and ProtectedRoute.tsx (the
// portal-wide slim banner shown on every authenticated page during an
// active trial) so the two can never drift into different wording for the
// same fact.
export function trialBannerMessage(daysRemaining: number): string {
  return `Trial — ${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`;
}

// === Entitlement facts (the "active" / cancels-at-period-end states) =======
// Describes what the tenant CAN DO, derived only from `entitlements` — never
// a plan/tier name (see this file's header). Order: the submissions cap
// first (the fact every tenant cares about most), then only the ADDITIONAL
// facts that are actually true/restrictive — a false `costOutputs` or a
// false `templatesOnly` isn't rendered as a "you can't..." line, since
// there's nothing designed for this page to say there beyond the positive
// facts it already lists.
export function entitlementFacts(
  entitlements: TenantConfig["entitlements"],
): string[] {
  const facts: string[] = [];
  const limit = entitlements.submitLimitPerMonth;
  facts.push(
    limit === null
      ? "Unlimited submissions per month"
      : `${limit} submission${limit === 1 ? "" : "s"} per month`,
  );
  if (entitlements.costOutputs) {
    facts.push("Cost estimates included");
  }
  facts.push(
    entitlements.editScope === "full"
      ? "Full design editor access"
      : "Limited design editor access",
  );
  if (entitlements.templatesOnly) {
    facts.push("Submissions must start from a template");
  }
  return facts;
}

// === Usage-this-month (GET /api/portal/usage) ===============================
export function describeUsage(usage: UsageResult): string {
  return usage.submitLimitPerMonth === null
    ? `${usage.submitCount} submitted this month (unlimited)`
    : `${usage.submitCount} of ${usage.submitLimitPerMonth} submitted this month`;
}

// "YYYY-MM" (UTC, per portalUsageHandler.ts) -> "August 2026". Constructed
// as a UTC date explicitly — parsing "YYYY-MM" bare would have JS assume UTC
// midnight but then *format* in the local zone, which can roll a
// near-midnight UTC instant into the wrong local calendar month; anchoring
// both construction AND formatting to UTC avoids that.
export function formatYearMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
    year: "numeric", month: "long", timeZone: "UTC",
  });
}

// === Renewal / period-end dates ==============================================
// currentPeriodEnd is Unix SECONDS (see api.ts's TenantConfig doc comment) —
// callers must check for its presence themselves (it's absent until a
// subscription webhook has landed) and never call this with `undefined`;
// keeping that check at the call site (rather than having this return a
// fallback string) is what makes "omit the line entirely when absent" the
// only possible outcome, per the spec's explicit instruction.
//
// Deliberately NOT UTC-anchored (contrast formatYearMonth above): this is a
// real moment in time, and every other date this codebase displays
// (LeadsPage/LeadDetailPage/SettingsPage/VersionHistoryPanel/ActivityFeedPanel
// — all `toLocaleDateString`/`toLocaleString` with no explicit `timeZone`)
// renders in the viewer's own local timezone, which is also the more useful
// answer for "when does my plan renew" than a UTC date would be. yearMonth
// above is different in kind — a fixed calendar-month identifier string, not
// an instant — which is why it alone pins UTC.
export function formatUnixSeconds(seconds: number): string {
  return new Date(seconds * 1000).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
}

// === Plan picker (no-subscription state) ====================================
// The ONE place a tier string is displayed — see this file's header for why
// that's fine here specifically. Fixed display order (mid before top) via a
// small rank map — this doesn't invent a plan name, it only orders the
// literal values the backend contract already fixes as a closed union.
const TIER_RANK: Record<BillingTier, number> = { basic: 0, mid: 1, top: 2 };

export interface TierPriceGroup {
  tier: BillingTier;
  entries: BillingPrice[];
}

// Groups the flat catalog (GET /api/portal/billing/prices) by tier, in a
// fixed tier order, preserving each entry's own place otherwise. A tier
// with only one interval configured (a partially-populated catalog) simply
// gets a one-entry group — callers render that as "no toggle, one option,"
// never a fabricated second interval.
export function groupPricesByTier(prices: BillingPrice[]): TierPriceGroup[] {
  const byTier = new Map<BillingTier, BillingPrice[]>();
  for (const price of prices) {
    const existing = byTier.get(price.tier);
    if (existing) existing.push(price);
    else byTier.set(price.tier, [price]);
  }
  return Array.from(byTier.entries())
    .map(([tier, entries]) => ({ tier, entries }))
    .sort((a, b) => TIER_RANK[a.tier] - TIER_RANK[b.tier]);
}

// The tier's MARKETING name, which is not its internal id. `mid`/`top` are
// what the database, the entitlement registry, the admin badges and every
// API payload use; "Pro"/"Unlimited" is what a customer is sold and what
// the pricing page says. Keeping the two separate is exactly what this
// module's header rule buys — a rename here needs no migration and touches
// no entitlement, because a raw tier string reaches a builder's eyes
// nowhere else.
//
// Deliberately an explicit map, not a capitalize: the previous
// implementation derived the label from the id, which silently made a
// rename impossible without renaming the tier itself.
//
// `basic` is absent because it is absent from `BillingTier` — only two
// tiers are sellable today. When Basic becomes self-serve it needs a price
// and a real submit cap first (its 50/month is still the D-2 placeholder),
// and adding it here is one entry.
const TIER_LABELS: Record<BillingTier, string> = {
  basic: "Basic",
  mid: "Pro",
  top: "Unlimited",
};

export function tierLabel(tier: BillingTier): string {
  return TIER_LABELS[tier];
}

export function intervalLabel(interval: BillingInterval): string {
  return interval === "monthly" ? "Monthly" : "Annual";
}

// Marketing copy for the two sellable tiers — UI-authored copy describing
// what's FOR SALE (see this file's header for why naming a tier here is
// fine, unlike everywhere else in this module). round7/signup-ui: LIFTED
// here from BillingPage.tsx's own former private `TIER_BLURBS` (unchanged
// text, carried over verbatim from the original round4 scaffold) so
// SignupPage.tsx's plan picker and BillingPage.tsx's plan picker can never
// silently diverge on how the same two tiers are described — both pickers
// are selling the exact same two products, just at different moments
// (before vs. after an account exists), and a future copy edit should be
// one change, not two kept in sync by hand. BillingPage.tsx now imports
// this instead of defining its own copy.
export const TIER_BLURBS: Record<BillingTier, string> = {
  basic: "Template-only visualizer and lead funnel, without estimate pricing. 100 submissions a month.",
  mid: "Full designer, instant quoting, and full estimate-pricing customization. 150 submissions a month.",
  // round12/self-serve-unlimited-only: this used to read "Everything in
  // Pro, plus…" — meaningless on a signup page that no longer shows Pro.
  // Spelled out instead; same entitlements (mid's, with no submit cap).
  top: "Full designer, instant quoting, and full estimate-pricing customization. Unlimited monthly submissions.",
};
